/**
 * @author taulmax
 * @date 2026.03.05
 * @description 가맹점별 판매 순위 테이블 컴포넌트
 */
import React from 'react';
import { IFranchiseSummaryItem } from '../../../../store/useSalesStatistics';

const PAGE_SIZE_OPTIONS = [
  { value: 20, label: '20건' },
  { value: 50, label: '50건' },
  { value: 100, label: '100건' },
  { value: 10000, label: '전체' },
];

interface IFranchiseRankingTableProps {
  franchiseRanking: IFranchiseSummaryItem[];
  pagedRanking: IFranchiseSummaryItem[];
  summary: {
    totalOrderCount: number;
    confirmedCount: number;
    totalOrderAmount: number;
    averageOrderAmount: number;
  };
  rankingPage: number;
  rankingPageSize: number;
  totalRankingPages: number;
  rankingPageNumbers: number[];
  setRankingPage: (page: number) => void;
  handleRankingPageSizeChange: (size: number) => void;
}

const FranchiseRankingTable = ({
  franchiseRanking,
  pagedRanking,
  summary,
  rankingPage,
  rankingPageSize,
  totalRankingPages,
  rankingPageNumbers,
  setRankingPage,
  handleRankingPageSizeChange,
}: IFranchiseRankingTableProps) => {
  return (
    <>
      <div className="!flex !justify-between !items-center !mb-4">
        <h3 className="!text-lg !font-semibold !text-gray-800">
          가맹점별 판매 순위
          {franchiseRanking.length > 0 && (
            <span className="!text-sm !font-normal !text-gray-500 !ml-2">
              (총 {franchiseRanking.length}개 가맹점)
            </span>
          )}
        </h3>
        <select
          value={rankingPageSize}
          onChange={e => handleRankingPageSizeChange(Number(e.target.value))}
          className="!px-3 !py-1.5 !rounded !border !border-gray-300 !text-sm !text-gray-700 !bg-white hover:!border-gray-400 focus:!outline-none focus:!border-blue-500"
        >
          {PAGE_SIZE_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {franchiseRanking.length > 0 ? (
        <>
          <div className="!overflow-x-auto">
            <table className="!w-full !text-sm">
              <thead>
                <tr className="!bg-gray-50 !border-b !border-gray-200">
                  <th className="!text-left !px-4 !py-3 !font-semibold !text-gray-700 !w-16">
                    순위
                  </th>
                  <th className="!text-left !px-4 !py-3 !font-semibold !text-gray-700">
                    가맹점 ID
                  </th>
                  <th className="!text-left !px-4 !py-3 !font-semibold !text-gray-700">
                    가맹점명
                  </th>
                  <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700">
                    총 주문건수
                  </th>
                  <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700">
                    확정건수
                  </th>
                  <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700">
                    총 매출금액
                  </th>
                  <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700">
                    건당 평균금액
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagedRanking.map((item, index) => (
                  <tr
                    key={item.franchiseId}
                    className="!border-b !border-gray-100 hover:!bg-gray-50"
                  >
                    <td className="!px-4 !py-3 !text-gray-600">
                      {rankingPage * rankingPageSize + index + 1}
                    </td>
                    <td className="!px-4 !py-3 !text-gray-500 !text-xs !font-mono">
                      {item.franchiseId}
                    </td>
                    <td className="!px-4 !py-3 !text-gray-900 !font-medium">
                      {item.companyName}
                    </td>
                    <td className="!px-4 !py-3 !text-right !text-gray-700">
                      {(item.totalOrderCount || 0).toLocaleString()}건
                    </td>
                    <td className="!px-4 !py-3 !text-right !text-blue-600 !font-medium">
                      {(item.confirmedCount || 0).toLocaleString()}건
                    </td>
                    <td className="!px-4 !py-3 !text-right !text-amber-600 !font-medium">
                      {(item.totalOrderAmount || 0).toLocaleString()}원
                    </td>
                    <td className="!px-4 !py-3 !text-right !text-green-600 !font-medium">
                      {(item.averageOrderAmount || 0).toLocaleString()}원
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="!bg-gray-100 !border-t-2 !border-gray-300">
                  <td
                    colSpan={3}
                    className="!px-4 !py-3 !font-bold !text-gray-800 !text-right"
                  >
                    합계
                  </td>
                  <td className="!px-4 !py-3 !text-right !font-bold !text-gray-700 !text-base">
                    {summary.totalOrderCount.toLocaleString()}건
                  </td>
                  <td className="!px-4 !py-3 !text-right !font-bold !text-blue-700 !text-base">
                    {summary.confirmedCount.toLocaleString()}건
                  </td>
                  <td className="!px-4 !py-3 !text-right !font-bold !text-amber-700 !text-base">
                    {summary.totalOrderAmount.toLocaleString()}원
                  </td>
                  <td className="!px-4 !py-3 !text-right !font-bold !text-green-700 !text-base">
                    {summary.averageOrderAmount.toLocaleString()}원
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* 페이지네이션 */}
          {totalRankingPages > 1 && (
            <div className="!flex !justify-center !items-center !gap-1 !mt-6">
              <button
                onClick={() => setRankingPage(0)}
                disabled={rankingPage === 0}
                className="!px-3 !py-2 !rounded !text-sm !font-medium !transition-colors disabled:!text-gray-300 disabled:!cursor-not-allowed !text-gray-600 hover:!bg-gray-100"
              >
                &laquo;
              </button>
              <button
                onClick={() => setRankingPage(rankingPage - 1)}
                disabled={rankingPage === 0}
                className="!px-3 !py-2 !rounded !text-sm !font-medium !transition-colors disabled:!text-gray-300 disabled:!cursor-not-allowed !text-gray-600 hover:!bg-gray-100"
              >
                &lsaquo;
              </button>
              {rankingPageNumbers.map(page => (
                <button
                  key={page}
                  onClick={() => setRankingPage(page)}
                  className={`!px-3 !py-2 !rounded !text-sm !font-medium !transition-colors !min-w-[36px] ${
                    rankingPage === page
                      ? '!bg-blue-500 !text-white'
                      : '!text-gray-600 hover:!bg-gray-100'
                  }`}
                >
                  {page + 1}
                </button>
              ))}
              <button
                onClick={() => setRankingPage(rankingPage + 1)}
                disabled={rankingPage >= totalRankingPages - 1}
                className="!px-3 !py-2 !rounded !text-sm !font-medium !transition-colors disabled:!text-gray-300 disabled:!cursor-not-allowed !text-gray-600 hover:!bg-gray-100"
              >
                &rsaquo;
              </button>
              <button
                onClick={() => setRankingPage(totalRankingPages - 1)}
                disabled={rankingPage >= totalRankingPages - 1}
                className="!px-3 !py-2 !rounded !text-sm !font-medium !transition-colors disabled:!text-gray-300 disabled:!cursor-not-allowed !text-gray-600 hover:!bg-gray-100"
              >
                &raquo;
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="!text-center !py-8 !text-gray-500">
          조회된 통계 데이터가 없습니다.
        </div>
      )}
    </>
  );
};

export default FranchiseRankingTable;
